/**
 * Source unique de la note client : l'**API REVIEWS.io** (`/product/review`, champ `stats`).
 *
 * Pourquoi pas les métafields : `reviewscouk.*` ne sait pas grouper les avis — confirmé par
 * le support REVIEWS.io. Or ici un produit = plusieurs SKU (formats × saveurs), et le widget
 * groupe par SKU + IDs de variantes. Sur `whey-native`, le métafield annonce 5 avis à 5,00
 * quand l'API en compte 302 à 4,18 ; ~70 produits actifs n'ont même aucun métafield alors
 * qu'ils ont des avis. Les étoiles restent rendues côté serveur depuis le métafield (peinture
 * immédiate, zéro CLS), mais ce module **réconcilie ensuite tout ce qui est faux** :
 *
 *   1. le compteur d'avis ;
 *   2. le remplissage des étoiles (sans quoi on affiche 5/5 sur un produit noté 4,18) ;
 *   3. l'`aggregateRating` JSON-LD, injecté ici et non plus émis en Liquid — baliser la
 *      valeur du métafield revenait à publier une note fausse, et une divergence entre note
 *      balisée et note affichée est ce qui fait retirer les extraits d'avis par Google.
 *
 * Perf : LAZY via IntersectionObserver → on ne fetch QUE les cartes qui entrent dans le
 * viewport (pas les carrousels/rows hors écran). + cache sessionStorage (1 fetch/produit
 * par session, cross-pages), dédup des requêtes en vol, concurrence limitée, requête
 * minimale (`per_page=1`, on ne lit que `stats`). Un MutationObserver rattrape les cartes
 * injectées après le 1er paint (Nosto, Section Rendering API).
 *
 * Exception au lazy : la fiche produit résout son groupement **immédiatement**, car le
 * balisage SEO ne doit pas dépendre du défilement du visiteur.
 */
const STORE = 'nutrimuscle-jp'; // clé publique REVIEWS.io (identique au widget)
const ENDPOINT = 'https://api.reviews.io/product/review';
const MAX_CONCURRENT = 4;

/**
 * @typedef {Object} Stats
 * @property {number} count - Nombre d'avis groupé.
 * @property {number} average - Note moyenne groupée.
 */

/** @type {Map<string, Promise<Stats|null>>} — dédup des requêtes en vol (même page) */
const inflight = new Map();
/** @type {Array<() => Promise<void>>} */
const queue = [];
let active = 0;

const pump = () => {
	while (active < MAX_CONCURRENT && queue.length) {
		const task = queue.shift();
		active++;
		task().finally(() => {
			active--;
			pump();
		});
	}
};

/**
 * @param {string} group - liste de SKU/ID séparés par `;` (même grouping que le widget)
 * @returns {Promise<Stats|null>}
 */
const fetchStats = (group) => {
	const cacheKey = `rio-stats:${group}`;

	try {
		const cached = sessionStorage.getItem(cacheKey);
		if (cached !== null) return Promise.resolve(JSON.parse(cached));
	} catch (_) {
		// sessionStorage indisponible (mode privé) ou entrée corrompue → on fetch quand même
	}

	if (inflight.has(group)) return inflight.get(group);

	const request = (async () => {
		try {
			const url = `${ENDPOINT}?store=${encodeURIComponent(STORE)}&sku=${encodeURIComponent(group)}&per_page=1`;
			const response = await fetch(url);
			if (!response.ok) return null;

			const data = await response.json();
			const count = data?.stats?.count;
			// `average` est renvoyé en String par l'API.
			const average = Number(data?.stats?.average);
			if (typeof count !== 'number' || !Number.isFinite(average)) return null;

			/** @type {Stats} */
			const stats = { count, average };

			try {
				sessionStorage.setItem(cacheKey, JSON.stringify(stats));
			} catch (_) {
				// ignore quota
			}
			return stats;
		} catch (error) {
			console.error('[reviews-io-rating]', error);
			return null;
		}
	})();

	inflight.set(group, request);
	return request;
};

/**
 * Réécrit le remplissage des 5 étoiles SSR.
 *
 * Chaque étoile est un SVG dont un `<clipPath>` borne la largeur du tracé plein. Les ids
 * générés en Liquid sont partagés entre étoiles de même remplissage (`star-clip-100`…) :
 * on ne peut donc pas modifier un `<rect>` sans risquer d'affecter les autres. On réattribue
 * un id unique par étoile avant de le retailler.
 *
 * @param {HTMLElement} el - racine `.product-rating`
 * @param {number} average
 */
const applyRating = (el, average) => {
	const stars = el.querySelectorAll('.product-rating__stars svg');
	if (stars.length === 0) return;

	for (const [index, star] of [...stars].entries()) {
		const rect = star.querySelector('clipPath rect');
		const clipPath = rect?.parentElement;
		const filled = star.querySelector('path[clip-path]');
		if (!rect || !clipPath || !filled) continue;

		const offset = Math.min(Math.max((average - index) * 100, 0), 100);
		const id = `star-clip-${index}-${Math.round(offset)}-${el.dataset.ratingUid}`;

		clipPath.id = id;
		rect.setAttribute('width', `${offset}%`);
		filled.setAttribute('clip-path', `url(#${id})`);
	}
};

/**
 * Injecte (ou met à jour) le nœud `aggregateRating`. Il porte le **même `@id`** que le nœud
 * produit émis par Shopify : au sens JSON-LD c'est le même nœud, les consommateurs fusionnent.
 *
 * @param {{type: string, id: string, name: string, url: string}} schema
 * @param {Stats} stats
 */
const injectSchema = (schema, stats) => {
	const existing = document.querySelector('script[type="application/ld+json"][data-rating-schema-output]');
	const node = existing ?? document.createElement('script');

	node.type = 'application/ld+json';
	node.dataset.ratingSchemaOutput = '';
	node.textContent = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': schema.type,
		'@id': schema.id,
		name: schema.name,
		url: schema.url,
		aggregateRating: {
			'@type': 'AggregateRating',
			ratingValue: Number(stats.average.toFixed(2)),
			bestRating: 5,
			worstRating: 1,
			ratingCount: stats.count,
		},
	});

	if (!existing) document.head.appendChild(node);
};

let uid = 0;

/** Révèle un bloc masqué en l'état (valeurs SSR) — utilisé en secours. */
const reveal = (el) => el.classList.remove('product-rating--awaiting');

/** @param {HTMLElement} el */
const reconcile = (el) => {
	const group = el.dataset.ratingGroup;
	if (!group) {
		// Pas de groupement exploitable : rien à réconcilier, on montre le SSR.
		reveal(el);
		return;
	}

	el.dataset.ratingUid ||= String(++uid);
	const target = el.querySelector('[data-rating-count-value]');

	// Filet de sécurité : si l'API tarde (file pleine, réseau lent), on révèle les
	// valeurs SSR plutôt que de laisser un blanc — la réconciliation s'appliquera
	// à son arrivée. Cas nominal : le cache sessionStorage résout bien avant.
	const failsafe = setTimeout(() => reveal(el), 3000);

	queue.push(async () => {
		const stats = await fetchStats(group);
		clearTimeout(failsafe);

		if (!stats) {
			// API en échec : un bloc sans données (--pending) se replie, un bloc
			// avec valeurs SSR (--awaiting) s'affiche tel quel.
			if (el.classList.contains('product-rating--pending')) el.style.display = 'none';
			reveal(el);
			return;
		}

		if (stats.count < 1) {
			// L'API — source de vérité — ne connaît aucun avis pour ce groupement.
			el.style.display = 'none';
			return;
		}

		if (target) target.textContent = String(stats.count);
		applyRating(el, stats.average);
		el.classList.remove('product-rating--pending');
		reveal(el);

		// Accordéon « Avis vérifiés » rendu masqué : les métafields Reviews.io du produit
		// sont absents (jamais écrits ou pas encore synchronisés) alors que l'API du groupe
		// a des avis — cas Bêta-Alanine, ticket 12781620201. On le révèle ici, l'API ayant
		// tranché : le widget qu'il contient affichera bien ces avis groupés.
		el.closest('[data-reviews-reveal][hidden]')?.removeAttribute('hidden');
	});
	pump();
};

const observer = new IntersectionObserver(
	(entries) => {
		for (const entry of entries) {
			if (!entry.isIntersecting) continue;
			observer.unobserve(entry.target);
			reconcile(/** @type {HTMLElement} */ (entry.target));
		}
	},
	{ rootMargin: '200px' }
);

/** Observe (lazy) les étoiles pas encore prises en charge. `data-rating-observed` évite les doublons. */
const scan = () => {
	const cards = document.querySelectorAll('.product-rating[data-rating-group]:not([data-rating-observed])');
	for (const el of cards) {
		el.dataset.ratingObserved = '1';
		// Dans un conteneur masqué (accordéon d'avis en attente de l'API), l'élément ne
		// croisera jamais l'IntersectionObserver : on réconcilie immédiatement.
		if (el.closest('[data-reviews-reveal][hidden]')) {
			reconcile(el);
			continue;
		}
		observer.observe(el);
	}
};

let scanTimer = null;
const scheduleScan = () => {
	clearTimeout(scanTimer);
	scanTimer = setTimeout(scan, 300);
};

/**
 * Fiche produit : le balisage SEO ne doit pas dépendre du défilement. On résout donc le
 * groupement du produit courant sans attendre l'IntersectionObserver.
 */
const bootstrapSchema = () => {
	const config = document.querySelector('script[type="application/json"][data-rating-schema]');
	if (!config) return;

	/** @type {{type: string, id: string, name: string, url: string, group: string}} */
	let schema;
	try {
		schema = JSON.parse(config.textContent);
	} catch (error) {
		console.error('[reviews-io-rating]', error);
		return;
	}
	if (!schema?.group) return;

	queue.push(async () => {
		const stats = await fetchStats(schema.group);
		// Un produit sans avis ne doit pas être balisé : Google rejette un aggregateRating vide.
		if (!stats || stats.count < 1) return;
		injectSchema(schema, stats);
	});
	pump();
};

bootstrapSchema();
scan();

// Cartes injectées après le 1er paint (Nosto — qui rend les cards du thème —, Section
// Rendering API, carrousels tardifs). Callback léger + debounce.
new MutationObserver((mutations) => {
	for (const m of mutations) {
		for (const node of m.addedNodes) {
			if (node.nodeType !== 1) continue;
			const el = /** @type {Element} */ (node);
			if (el.matches?.('.product-rating[data-rating-group]') || el.querySelector?.('.product-rating[data-rating-group]')) {
				scheduleScan();
				return;
			}
		}
	}
}).observe(document.body, { childList: true, subtree: true });
